// Bulletproof Auto Add-to-Cart for Savor Theme
document.addEventListener('DOMContentLoaded', () => {
  const FREE_PRODUCT_VARIANT_ID = 51306351591730;
  const SUBTOTAL_THRESHOLD = 5000;
  let isUpdatingCart = false;

  const rootPath = window.Shopify?.routes?.root || '/';
  const origin = window.location.origin;
  const fix = url => url.replace(/([^:]\/)\/+/g, "$1");

  const cartJsUrl      = fix(`${origin}${rootPath}cart.js`);
  const addJsUrl       = fix(`${origin}${rootPath}cart/add.js`);
  const changeJsUrl    = fix(`${origin}${rootPath}cart/change.js`);
  const cartSectionUrl = fix(`${origin}${rootPath}cart?sections=cart-drawer`);

  // ─── Inject CSS to hide qty controls for gift item ───────────────────────
  // Savor renders cart items with data-variant-id on the line item element.
  // This hides the stepper and remove button only for the gift variant.
  const style = document.createElement('style');
  style.textContent = `
    /* Lock qty buttons */
    cart-quantity-selector-component[data-variant-id="${FREE_PRODUCT_VARIANT_ID}"] button {
      pointer-events: none;
      opacity: 0.35;
      cursor: not-allowed;
    }
    cart-quantity-selector-component[data-variant-id="${FREE_PRODUCT_VARIANT_ID}"] input {
      pointer-events: none;
      opacity: 0.65;
    }
    /* Hide remove button — scoped via :has() to avoid touching other line items */
    .cart-items__quantity-controls:has(cart-quantity-selector-component[data-variant-id="${FREE_PRODUCT_VARIANT_ID}"]) .cart-items__remove {
      display: none !important;
    }
  `;
  document.head.appendChild(style);

  // ─── Intercept native fetch (loop-safe) ───────────────────────────────────
  const nativeFetch = window.fetch;
  window.fetch = async function(...args) {
    if (args[1]?.headers?.['X-Requested-With'] === 'GiftScriptBot') {
      return nativeFetch.apply(this, args);
    }

    const urlString = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');

    // Block manual qty changes / removals of the gift variant
    if (
      (urlString.includes('/cart/change') || urlString.includes('/cart/update')) &&
      args[1]?.body
    ) {
      try {
        const body = JSON.parse(args[1].body);
        const targetId = body.id || body.variant_id;
        // If someone's trying to manually touch the gift item, silently block it
        if (String(targetId) === String(FREE_PRODUCT_VARIANT_ID)) {
          console.log('GiftScript: blocked manual change to gift item');
          // Return a fake successful response so the theme doesn't error out
          return new Response(JSON.stringify({ status: 'blocked' }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          });
        }
      } catch(e) {
        // Body wasn't JSON (e.g. form-encoded) — let it through and catch below
      }
    }

    const response = await nativeFetch.apply(this, args);

    if (
      urlString.includes('/cart/add') ||
      urlString.includes('/cart/change') ||
      urlString.includes('/cart/clear')
    ) {
      setTimeout(checkCartAndAddFreebie, 400);
    }

    return response;
  };

  // ─── Cart check ───────────────────────────────────────────────────────────
  function checkCartAndAddFreebie() {
    if (isUpdatingCart) return;
    nativeFetch(cartJsUrl)
      .then(r => r.json())
      .then(cart => {
        const giftItem = cart.items.find(item => item.variant_id === FREE_PRODUCT_VARIANT_ID);
        const giftInCart = !!giftItem;
        let subtotalMinusGift = cart.total_price;
        if (giftInCart) subtotalMinusGift -= giftItem.final_line_price;

        // Also correct any quantity drift (e.g. someone added 2 via URL manipulation)
        if (giftInCart && giftItem.quantity !== 1 && subtotalMinusGift >= SUBTOTAL_THRESHOLD) {
          isUpdatingCart = true;
          nativeFetch(changeJsUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'GiftScriptBot' },
            body: JSON.stringify({ id: String(FREE_PRODUCT_VARIANT_ID), quantity: 1 })
          }).then(() => { isUpdatingCart = false; refreshSavorCartDrawer(); });
          return;
        }

        if (subtotalMinusGift >= SUBTOTAL_THRESHOLD && !giftInCart) {
          isUpdatingCart = true;
          updateCartItems(addJsUrl, { items: [{ id: FREE_PRODUCT_VARIANT_ID, quantity: 1 }] });
        } else if (subtotalMinusGift < SUBTOTAL_THRESHOLD && giftInCart) {
          isUpdatingCart = true;
          updateCartItems(changeJsUrl, { id: String(FREE_PRODUCT_VARIANT_ID), quantity: 0 });
        }
      })
      .catch(e => console.error('GiftScript: cart check failed', e));
  }

  function updateCartItems(url, data) {
    nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'GiftScriptBot' },
      body: JSON.stringify(data)
    })
    .then(r => r.json())
    .then(() => { isUpdatingCart = false; refreshSavorCartDrawer(); })
    .catch(err => { isUpdatingCart = false; console.error('GiftScript: cart update failed', err); });
  }

  // ─── Refresh — layered, no reload ────────────────────────────────────────
  function refreshSavorCartDrawer() {
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch(e) {}

    import('@theme/events')
      .then(({ CartUpdateEvent }) => {
        nativeFetch(cartJsUrl).then(r => r.json()).then(cart => {
          document.dispatchEvent(new CartUpdateEvent(cart, 'manual-trigger', {
            itemCount: cart.item_count, source: 'gift-script', sections: {}
          }));
        });
      })
      .catch(() => refreshViaSectionsApi());
  }

  function refreshViaSectionsApi() {
    nativeFetch(cartSectionUrl)
      .then(r => r.json())
      .then(sections => {
        const htmlString = sections['cart-drawer'];
        if (!htmlString) return;
        const parsedDoc = new DOMParser().parseFromString(htmlString, 'text/html');
        const newDrawer = parsedDoc.querySelector('cart-drawer');
        const currentDrawer = document.querySelector('cart-drawer');
        if (newDrawer && currentDrawer) {
          ['active', 'open', 'is-open'].forEach(cls => {
            if (currentDrawer.classList.contains(cls)) newDrawer.classList.add(cls);
          });
          currentDrawer.replaceWith(newDrawer);
        }
      })
      .catch(e => console.error('GiftScript: sections API swap failed', e));
  }

  checkCartAndAddFreebie();
});